import cloneDeep from "lodash/cloneDeep";
import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';
import tableService from '@/components/base_components/tables/services/table-service';
import Vue from 'vue';
import FILTER_CARD_STATES from '@/components/base_components/tables/filter/FILTER_CARD_STATES';

export default {
  computed: {
    cachedTable() {
      return this.getCachedTable();
    }
  },
  methods: {
    getCachedTable() {
      return this.value;
    },
    tableNotCachedYet() {
      return this.value == undefined;
    },
    resetCachedTable() {
      let tableData = cloneDeep(this.value);
      tableData = this._setFiltersObj({}, tableData);
      tableData = this._setColumnsObj({}, tableData);
      tableData.preset = {
        name: '',
        saveColumns: false,
        isDefault: false,
      };
      this.$emit('input', tableData);
    },
    /**************************
     * ### CACHE SETTERS ###
     **************************/
    setCachedExistingPreset(preset) {
      if (preset == undefined) { return this.setCachedPresetObj({}); }

      const { columnObj, filterObj, presetObj } = tableService.getColumnsAndFiltersOf(preset);

      let tableData = cloneDeep(this.value);
      if (columnObj != undefined) {
        tableData = this._setColumnsObj(columnObj, tableData);
      }
      tableData = this._setFiltersObj(filterObj, tableData);
      tableData.preset = presetObj;
      if(tableData[TABLE_DATA_KEYS.FILTER_CARD_STATE] === FILTER_CARD_STATES.NEW) {
        tableData[TABLE_DATA_KEYS.FILTER_CARD_STATE] = FILTER_CARD_STATES.SELECT
      }
      this.$emit('input', tableData);
    },
    setCachedFilterCardState(state) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.FILTER_CARD_STATE, state);
    },
    setCachedFilterOrder(order) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.FILTER_ORDER, order);
    },
    setCachedFilterValue(key, value) {
      if (this.tableNotCachedYet()) { return }
      const tableData = cloneDeep(this.value);

      const valueIsEmpty = tableService.isEmptyFilterValue(value);
      const filterKeyExists = tableData[TABLE_DATA_KEYS.FILTERS][key] != undefined;

      if (valueIsEmpty && filterKeyExists) {
        delete tableData[TABLE_DATA_KEYS.FILTERS][key];
      } else if (valueIsEmpty === false) {
        tableData[TABLE_DATA_KEYS.FILTERS][key] = value;
      }

      this.$emit('input', tableData);
    },
    setCachedHeaderOrder(order) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.HEADER_ORDER, order);
    },
    setCachedNotVisibleFilters(notVisibleFilters) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS, notVisibleFilters);
    },
    setCachedNotVisibleHeaders(notVisibleHeaders) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS, notVisibleHeaders);
    },
    setCachedPresetColumnSave(saveColumns) {
      this._setPresetValue('saveColumns', saveColumns);
    },
    setCachedPresetIsDefault(presetIsDefault) {
      this._setPresetValue('isDefault', presetIsDefault);
    },
    setCachedPresetName(name) {
      this._setPresetValue('name', name);
    },
    async setCachedPresetObj(presetObj) {
      const newPresetObj = {
        id: presetObj.id ?? null,
        name: presetObj.name ?? '',
        query: presetObj.query ?? '',
        isDefault: presetObj.isDefault ?? false,
        saveColumns: presetObj.saveColumns ?? false,
      }
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.PRESET, newPresetObj);
    },
    setCachedSort({ sortBy, sortDesc }) {
      if (this.tableNotCachedYet()) { return }

      const tableData = cloneDeep(this.value);
      tableData[TABLE_DATA_KEYS.SORT_BY] = sortBy;
      tableData[TABLE_DATA_KEYS.SORT_BY_DESC] = sortDesc;
      this.$emit('input', tableData);
    },
    setCachedSearchValue(value) {
      if (this.tableNotCachedYet()) { return }

      const tableData = cloneDeep(this.value);
      tableData[TABLE_DATA_KEYS.SEARCH_EXPRESSION] = value;
      this.$emit('input', tableData);
    },
    setCachedTruncateData(truncateData) {
      this._setTableValueAndEmitInput(TABLE_DATA_KEYS.TRUNCATE_DATA, truncateData);
    },
    setCachedTruncateOptions({ key, value }) {
      const tableData = cloneDeep(this.value);

      if (value === undefined) {
        Vue.delete(tableData[TABLE_DATA_KEYS.TRUNCATE_OPTIONS], key);
        return this.$emit('input', tableData);
      }

      if (tableData[TABLE_DATA_KEYS.TRUNCATE_OPTIONS] == undefined) {
        Vue.set(tableData, TABLE_DATA_KEYS.TRUNCATE_OPTIONS, {});
      }

      tableData[TABLE_DATA_KEYS.TRUNCATE_OPTIONS][key] = value;
      this.$emit('input', tableData);
    },
    /**************************
     * ### CACHE HELPERS ###
     **************************/
    _setTableValueAndEmitInput(key, value) {
      if (this.tableNotCachedYet()) { return }
      const tableData = cloneDeep(this.value);
      tableData[key] = value;
      this.$emit('input', tableData);
    },
    _setPresetValue(key, value) {
      if (this.tableExists === false) { return }
      const tableData = cloneDeep(this.value);
      tableData[TABLE_DATA_KEYS.PRESET][key] = value;
      this.$emit('input', tableData);
    },
    _setFiltersObj(filtersObj, table) {
      table[TABLE_DATA_KEYS.FILTERS] = filtersObj?.[TABLE_DATA_KEYS.FILTERS] ?? { };
      table[TABLE_DATA_KEYS.FILTER_ORDER] = filtersObj?.[TABLE_DATA_KEYS.FILTER_ORDER] ?? [];
      table[TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS] = filtersObj?.[TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS] ?? [];
      table[TABLE_DATA_KEYS.TRUNCATE_DATA] = filtersObj?.[TABLE_DATA_KEYS.TRUNCATE_DATA] ?? true;
      table[TABLE_DATA_KEYS.TRUNCATE_OPTIONS] = filtersObj?.[TABLE_DATA_KEYS.TRUNCATE_OPTIONS] ?? {};

      return table;
    },
    _setColumnsObj(columnsObj, table) {
      table[TABLE_DATA_KEYS.SORT_BY] = columnsObj?.[TABLE_DATA_KEYS.SORT_BY] ?? [];
      table[TABLE_DATA_KEYS.SORT_BY_DESC] = columnsObj?.[TABLE_DATA_KEYS.SORT_BY_DESC] ?? [];
      table[TABLE_DATA_KEYS.HEADERS] = columnsObj?.[TABLE_DATA_KEYS.HEADERS] ?? [];
      table[TABLE_DATA_KEYS.HEADER_ORDER] = columnsObj?.[TABLE_DATA_KEYS.HEADER_ORDER] ?? [];
      table[TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS] = columnsObj?.[TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS] ?? [];
      return table;
    },
  },
}
